import { Injectable } from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';

import { notifyMessage } from '../../shared/constants/notify-message';
import {
  SupplierResponseInterface,
  PutSupplierObjectInterface,
} from '../../shared/interfaces/survey.interface';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { LayoutConstants } from './layout-constants';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { map } from 'rxjs/operators';
import { SurveyMetaDataService } from '../../shared/services/survey-meta-data/survey-meta-data.service';
import { UpdateSupplierPayloadInterface } from '../../shared/interfaces/survey-meta-data.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { GroupedSupplierOverride } from './edit-suppliers/domain/grouped-supplier-override';

@Injectable({
  providedIn: 'root',
})
export class EditSupplierModalService {
  constructor(
    private _modal: ModalService,
    private _toastr: ToasterService,
    private _buyerApiService: BuyerApiService,
    private _surveyMetaDataService: SurveyMetaDataService
  ) {}

  async updateSuppliers(
    surveyId: number,
    supplierPayload: UpdateSupplierPayloadInterface,
    thresholdWarning = false,
    doFeasibilityCall = true
  ) {
    new GroupedSupplierOverride(supplierPayload).cpi();
    this._surveyMetaDataService.setPurePriceRateCardEnable(
      !!supplierPayload.isPurePriceRateCardEnable
    );

    if (supplierPayload.flexValue > 100) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_PAYLOAD
          .FLEXIBILITY_GREATER_THAN_HUNDRED
      );
      return false;
    }
    if (!supplierPayload.suppliers[0].scpi) {
      if (!supplierPayload.isCpiConfirmed) {
        this._toastr.error(
          notifyMessage.errorMessage.SUPPLIER_PAYLOAD.CPI_VALUE_CONFIRMED
        );
        return false;
      }
    }

    if (supplierPayload.totalAllocations < supplierPayload.completesNeeded) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_PAYLOAD
          .ALLOCATION_LESS_THAN_COMPLETES
      );
      return false;
    } else if (
      supplierPayload.totalAllocations > supplierPayload.completesNeeded
    ) {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: LayoutConstants.CONFIRM_MODAL.EDIT_SUPPLIER.TEXT,
          confirmButtonLabel:
            LayoutConstants.CONFIRM_MODAL.EDIT_SUPPLIER.CONFIRM_BUTTON_TEXT,
          cancelButtonLabel:
            LayoutConstants.CONFIRM_MODAL.EDIT_SUPPLIER.CANCEL_BUTTON_TEXT,
        },
      });
      const isUpdate = await modalRef.onClose$
        .pipe(map((msg) => msg === 'ok'))
        .toPromise();
      if (isUpdate) {
        const response = await this.updateSupplierList(
          surveyId,
          supplierPayload
        );
        if (doFeasibilityCall) {
          this._surveyMetaDataService.sendSuppliersSelected({
            suppliers: response,
            blend_id: supplierPayload.blend_id,
          });
        }
        if (thresholdWarning) {
          this._toastr.warning(
            notifyMessage.CPI_PAUSE_THRESHOLD_WARNING,
            'WARNING',
            { enableHtml: true } as any
          );
        } else {
          this._toastr.success(notifyMessage.successMessage.SUPPLIERS_UPDATED);
        }

        return true;
      }
      return false;
    } else {
      const response = await this.updateSupplierList(surveyId, supplierPayload);
      if (doFeasibilityCall) {
        this._surveyMetaDataService.sendSuppliersSelected({
          suppliers: response,
          blend_id: supplierPayload.blend_id,
        });
      }

      if (thresholdWarning) {
        this._toastr.warning(
          notifyMessage.CPI_PAUSE_THRESHOLD_WARNING,
          'WARNING',
          { enableHtml: true } as any
        );
      } else {
        this._toastr.success(notifyMessage.successMessage.SUPPLIERS_UPDATED);
      }

      return true;
    }
  }

  public updateSupplierList(
    surveyId: number,
    supplierPayload: UpdateSupplierPayloadInterface
  ) {
    const supplierList = this._mapSupplierList(supplierPayload.suppliers);
    return this._buyerApiService
      .putSuppliers(
        surveyId,
        'editable',
        supplierList,
        supplierPayload.operatorOverrideMdl,
        supplierPayload.manualOverrideMdl,
        supplierPayload.surveyPauseThreshold,
        undefined,
        undefined,
        undefined,
        supplierPayload.blend_id,
        supplierPayload.manualScpiOverrideEnabled,
        supplierPayload.isPurePriceRateCardEnable,
        undefined,
        supplierPayload.defaultPurePriceOverride
      )
      .toPromise();
  }

  private _isGroupExists(supplierList: SupplierResponseInterface[]) {
    return supplierList.some((supplier) => {
      return supplier.isChecked && supplier.group_name;
    });
  }

  private _mapSupplierList(
    supplierList: SupplierResponseInterface[]
  ): PutSupplierObjectInterface[] {
    return supplierList
      .filter((supplier) => supplier.is_active)
      .map((supplier) => this._mapSupplier(supplier));
  }

  private _mapSupplier(
    supplier: SupplierResponseInterface
  ): PutSupplierObjectInterface {
    const supplierObj: PutSupplierObjectInterface = {
      cpi: Number(supplier.cpi),
      bid_cpi: supplier.bid_cpi ? parseFloat(supplier.bid_cpi) : null,
      current_target: supplier.current_target,
      completes_goal: supplier.max,
      quantity: supplier.quantity,
      flexValue: supplier.flexValue,
      scpi: supplier.scpi ? supplier.scpi : 0,
      group_name: supplier.group_name || '',
      is_custom_allocation: supplier.is_custom_allocation,
      suppliers: !supplier.group_name
        ? [supplier.suppliers[0]]
        : supplier.suppliers,
    };
    if (supplier.group_ref) {
      supplierObj.group_ref = supplier.group_ref;
    }
    return supplierObj;
  }
}
